<template>
  <div class="input-wrapper">
    <div class="input-icon">
      <slot name="icon"></slot>
    </div>
    <input v-if="isNumeric" ref="numberInput" v-model="numericValue" :class="inputClasses" :placeholder="initializedPlaceholder" type="number" @blur="onBlurNumber" @input="onNumberUpdated" />
    <input v-if="!isNumeric" v-model="textValue" :class="inputClasses" :placeholder="initializedPlaceholder" type="text" @focus="onFocusText"  />
  </div>
</template>

<script>
'use strict';

// Imports.
import { computed, ref, toRefs } from 'vue';
import { ToWords } from 'to-words';

// Define this component for export.
export default {
  props: {
    classes: {
      type: Array,
      required: false
    },
    placeholder: {
      type: String,
      required: false
    },
    modelValue: {
      type: String,
      required: true
    },
    showText: {
      type: Boolean,
      required: false,
      default: true
    }
  },

  // Perform component setup.
  setup(props) {
    const { placeholder, modelValue } = toRefs(props);
    const initializedPlaceholder = computed(() => {
      if (placeholder) {
        return placeholder.value;
      } else {
        return 'Enter a number.';
      }
    });
    let delimitedValue = modelValue.value || '0';
    delimitedValue = delimitedValue.toString().split('.');
    delimitedValue[0] = delimitedValue[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + ',');
    delimitedValue = delimitedValue.join('.');
    return {
      initializedPlaceholder,
      toWords: new ToWords({
        localeCode: 'en-US',
        converterOptions: {
          currency: false,
          ignoreDecimal: false,
          ignoreZeroCurrency: true
        }
      }),
      numericValue: ref(modelValue.value),
      textValue: ref(delimitedValue),
      isNumeric: ref(false)
    };
  },

  updated() {
    if (this.isNumeric) {
      this.$refs.numberInput.focus();
    }
  },

  computed: {
    inputClasses() {
      if (this.classes && this.classes.length > 0) {
        return this.classes.join(' ');
      } else {
        return 'number-input';
      }
    }
  },

  methods: {
    onNumberUpdated(event) {
      this.$emit('update:modelValue', event.target.value);
      this.textValue = this.delimit(this.numericValue);
    },
    onBlurNumber() {
      this.isNumeric = false;
    },
    onFocusText() {
      this.isNumeric = true;
    },
    delimit(value) {
      value = value.toString().split('.');
      value[0] = value[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + ',');
      return value.join('.');
    }
  },

  // Watch for a change in the underlying value of this number input.
  // When a change is detected, update the display state.
  watch: {
    modelValue: {
      deep: true,
      handler: function (newValue) {
        this.numericValue = newValue;
        this.textValue = this.delimit(newValue);
      }
    }
  }
};
</script>

<style lang="css" scoped>
.input-icon {
  position: absolute;
  display: flex;
  width: 34px;
  height: 38px;
  padding-left: 4px;
  justify-content: center;
  align-items: center;
  border-right: 1px solid rgba(var(--text-color-rgba), 1);
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
  background-color: rgba(var(--background-color-nft), 1);
  z-index: 1;
}
.number-input {
  position: relative;
  height: 20px;

  margin-bottom: 0px;
  padding: 10px;

  border-style: solid;
  border-width: 1px;
  border-color: rgba(var(--text-color-rgba), 0.2);
  border-radius: 20px;
  background-color: transparent;
  font-size: 15px;
  font-family: "WorkSans-Regular", sans-serif;
  color: rgba(var(--text-color-rgba), 0.8);
  outline: none;
  transition: all 200ms cubic-bezier(0.215, 0.61, 0.355, 1);

  height: 20px;
  width: calc(100% - 40px);
  padding: 8px 12px;
  padding-left: 40px;
  line-height: 1.4;
  color: var(--text-color);
  background-color: var(--background-accent);
  border: 1px solid rgba(var(--purple-accent-rgb), 0.2);
  transition: border-color 200ms ease;
  border-radius: 50px;
  font-style: initial;
}

.number-input:hover {
  border-color: rgba(var(--purple-accent-rgb), 0.5);
}

.number-input:focus {
  outline: none;
  border-width: 2px;
  border-color: rgba(var(--purple-accent-rgb), 1);
}

.number-input::-webkit-outer-spin-button,
.number-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
.number-input[type='number'] {
  -moz-appearance: textfield;
}
</style>
